import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NotificationItem } from '@isbd/shared';
import { kuunda, kuundaDbSchema } from '@/lib/kuunda';
import { useAuth } from '@/features/auth/AuthProvider';
import { ListSkeleton } from '@/features/ui/skeleton';
import './notifications.css';

const KIND_LABELS: Record<string, string> = {
  general: 'Info',
  alert: 'Alerte',
  information: 'Information',
  orientation_technique: 'Orientation technique',
  preoccupation_created: 'Nouvelle préoccupation',
  preoccupation_reply: 'Réponse assistance',
  marketplace_inquiry: 'Marketplace',
};

const PRIORITY_LABELS: Record<string, string> = {
  low: 'Faible',
  medium: 'Moyenne',
  high: 'Importante',
  critical: 'Critique',
};

function kindLabel(kind: string) {
  return KIND_LABELS[kind] ?? kind;
}

function priorityLabel(priority: string) {
  return PRIORITY_LABELS[priority] ?? priority;
}

function notificationHref(n: NotificationItem, isStaff: boolean): string | null {
  switch (n.kind) {
    case 'preoccupation_created':
      return isStaff ? '/admin/assistance' : '/app/assistance';
    case 'preoccupation_reply':
      return '/app/assistance';
    case 'marketplace_inquiry':
      return '/app/marketplace-crm';
    case 'alert':
    case 'general':
      return isStaff ? '/admin/assistance' : '/app';
    default:
      return null;
  }
}

export function NotificationBell() {
  const { profile, isStaff } = useAuth();
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);

  async function load() {
    if (!profile) return;
    setLoading(true);
    const { data } = await kuunda
      .from('notifications')
      .select('*')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false })
      .limit(30);
    setItems((data ?? []) as NotificationItem[]);
    setLoading(false);
  }

  useEffect(() => {
    void load();
    if (!profile) return;

    let realtimeOk = false;
    const pollMs = 45_000;
    const pollId = window.setInterval(() => {
      if (!realtimeOk) void load();
    }, pollMs);

    const channel = kuunda
      .channel(`notif-${profile.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: kuundaDbSchema, table: 'notifications', filter: `user_id=eq.${profile.id}` },
        () => {
          void load();
        },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: kuundaDbSchema, table: 'notifications', filter: `user_id=eq.${profile.id}` },
        () => {
          void load();
        },
      );

    void channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') realtimeOk = true;
    });

    return () => {
      window.clearInterval(pollId);
      channel.unsubscribe();
    };
  }, [profile]);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = items.filter((n) => !n.is_read).length;

  async function markRead(id: string) {
    await kuunda.from('notifications').update({ is_read: true, status: 'read' }).eq('id', id);
    setItems((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true, status: 'read' } : n)),
    );
  }

  async function markAll() {
    if (!profile) return;
    await kuunda
      .from('notifications')
      .update({ is_read: true, status: 'read' })
      .eq('user_id', profile.id)
      .eq('is_read', false);
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true, status: 'read' })));
  }

  async function openNotification(n: NotificationItem) {
    if (!n.is_read) await markRead(n.id);
    setOpen(false);
    const href = notificationHref(n, Boolean(isStaff));
    if (href) navigate(href);
  }

  return (
    <div className="notif-wrap" ref={wrapRef}>
      <button
        type="button"
        className="notif-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        aria-expanded={open}
      >
        🔔
        {unread > 0 ? <span className="notif-badge">{unread > 99 ? '99+' : unread}</span> : null}
      </button>
      {open ? (
        <div className="notif-panel" role="dialog" aria-label="Liste des notifications">
          <div className="notif-head">
            <strong>Notifications</strong>
            <button type="button" className="btn btn-ghost" onClick={() => void markAll()} disabled={unread === 0}>
              Tout lu
            </button>
          </div>
          {loading && items.length === 0 ? <ListSkeleton rows={3} /> : null}
          {!loading && items.length === 0 ? <p className="muted">Aucune notification.</p> : null}
          <ul>
            {items.map((n) => (
              <li key={n.id} className={n.is_read ? '' : 'unread'}>
                <button type="button" onClick={() => void openNotification(n)}>
                  <span className="chip">{kindLabel(n.kind)}</span>
                  {n.subject ? (
                    <strong style={{ display: 'block', marginBottom: '0.2rem' }}>{n.subject}</strong>
                  ) : null}
                  <p>{n.body}</p>
                  <small>
                    {n.priority ? `${priorityLabel(n.priority)} · ` : ''}
                    {new Date(n.created_at).toLocaleString('fr-FR')}
                  </small>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
